import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconChevronDown, IconInbox, IconSend } from "@tabler/icons-react";
import type { EmailDirectionFilter, EmailSentByFilter } from "../types";
import { useMailboxContext } from "../../emails/useMailboxContext";
import {
  useReceivablesEmailThreadQuery,
  useReceivablesEmailsQuery,
} from "../../../api/queries/receivablesQueries";
import { useMentionableOrgUsersQuery } from "../../../api/queries/orgUsersQueries";
import type { Email } from "../../../utils/interfaces";
import type { ReplyMode } from "../../../utils/replyRecipients";
import { EmailReplyModal } from "./EmailReplyModal";
import { EmailThreadListRow } from "./EmailThreadListRow";
import { EmailThreadReadingPane } from "./EmailThreadReadingPane";
import { ReceivablesPagination } from "../ReceivablesPagination";
import { useMailboxSyncNow } from "../../../hooks/useMailboxSyncNow";
import { useEmailInboxRefreshFeedback } from "../../../hooks/useEmailInboxRefreshFeedback";
import { EmailMailboxRefreshControl } from "./EmailMailboxRefreshControl";
import { OutlookSubscriptionDebugPanel } from "./OutlookSubscriptionDebugPanel";
import { useAuth } from "../../../hooks/useAuth";

type ReplyTarget = { message: Email; mode: ReplyMode };

const EMAILS_PAGE_SIZE = 25;

function EmailDirectionTabs({
  direction,
  onDirectionChange,
}: {
  direction: EmailDirectionFilter;
  onDirectionChange: (direction: EmailDirectionFilter) => void;
}) {
  const tabs: Array<{
    id: EmailDirectionFilter;
    label: string;
    icon: typeof IconInbox;
  }> = [
    { id: "incoming", label: "Inbox", icon: IconInbox },
    { id: "outgoing", label: "Sent", icon: IconSend },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map(({ id, label, icon: Icon }) => {
        const active = direction === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onDirectionChange(id)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              active
                ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
            }`}
          >
            <Icon
              size={16}
              className={active ? "text-teal-700" : "text-slate-400"}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function EmailSentByFilter({
  value,
  onChange,
  users,
}: {
  value: EmailSentByFilter;
  onChange: (value: EmailSentByFilter) => void;
  users: Array<{ id: number; username: string }>;
}) {
  return (
    <div className="relative shrink-0">
      <select
        id="receivables-email-sent-by-filter"
        value={
          value === ""
            ? ""
            : value === "automated"
              ? "automated"
              : String(value)
        }
        onChange={(event) => {
          const next = event.target.value;
          if (next === "") onChange("");
          else if (next === "automated") onChange("automated");
          else onChange(Number(next));
        }}
        className="h-8 min-w-36 appearance-none rounded-md border border-slate-200 bg-white py-1 pl-2 pr-7 text-sm text-slate-700"
        aria-label="Filter by sender"
      >
        <option value="">All senders</option>
        <option value="automated">Automated</option>
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.username}
          </option>
        ))}
      </select>
      <IconChevronDown
        size={14}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
        aria-hidden
      />
    </div>
  );
}

export function ReceivablesEmailsInbox({
  search,
  page,
  onPageChange,
  filterSentBy = "",
  onFilterSentByChange,
  onOpenInvoice,
  onOpenCustomer,
}: {
  search?: string;
  page: number;
  onPageChange: (page: number) => void;
  filterSentBy?: EmailSentByFilter;
  onFilterSentByChange?: (value: EmailSentByFilter) => void;
  onOpenInvoice?: (invoiceId: string) => void;
  onOpenCustomer?: (customerId: number) => void;
}) {
  const navigate = useNavigate();
  const { canMutateOrg } = useAuth();
  const mailboxContext = useMailboxContext();
  const [direction, setDirection] = useState<EmailDirectionFilter>("incoming");
  const [localSentBy, setLocalSentBy] = useState<EmailSentByFilter>("");
  const sentBy = onFilterSentByChange ? filterSentBy : localSentBy;
  const setSentBy = onFilterSentByChange ?? setLocalSentBy;
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const [userRefreshStarted, setUserRefreshStarted] = useState(false);
  const sync = useMailboxSyncNow({ notify: "silent" });

  const { data: mentionableUsers = [] } = useMentionableOrgUsersQuery();

  const sentByParam =
    direction === "outgoing" && sentBy !== "" ? sentBy : undefined;

  const listQuery = useReceivablesEmailsQuery({
    page,
    pageSize: EMAILS_PAGE_SIZE,
    search,
    direction,
    sentBy: sentByParam,
  });

  const isListRefetching = userRefreshStarted && listQuery.isFetching;

  const refreshFeedback = useEmailInboxRefreshFeedback({
    isTriggeringSync: sync.isTriggeringSync,
    isSyncing: sync.isSyncing,
    isProcessingEmails: sync.isProcessingEmails,
    hasError: Boolean(sync.syncStatus?.has_error),
    isListRefetching,
  });

  const hasSyncError = Boolean(sync.syncStatus?.has_error);
  const isRefreshActiveForUi =
    refreshFeedback.isRefreshActive ||
    (userRefreshStarted && !refreshFeedback.showSuccess && !hasSyncError);

  useEffect(() => {
    if (refreshFeedback.showSuccess || hasSyncError) {
      setUserRefreshStarted(false);
    }
  }, [refreshFeedback.showSuccess, hasSyncError]);

  const handleRefresh = () => {
    setUserRefreshStarted(true);
    sync.handleSyncNow();
  };

  const detailQuery = useReceivablesEmailThreadQuery(selectedThreadId);

  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / EMAILS_PAGE_SIZE));

  const selectedRow = useMemo(
    () => rows.find((row) => row.gmailThreadId === selectedThreadId),
    [rows, selectedThreadId],
  );

  const threadSubject =
    detailQuery.data?.emails?.[detailQuery.data.emails.length - 1]?.subject ??
    selectedRow?.subject ??
    "";

  useEffect(() => {
    setSelectedThreadId(null);
  }, [direction, sentBy]);

  const handleDirectionChange = (next: EmailDirectionFilter) => {
    setDirection(next);
    onPageChange(1);
    if (next === "incoming") {
      setSentBy("");
    }
  };

  const handleSentByChange = (next: EmailSentByFilter) => {
    setSentBy(next);
    onPageChange(1);
  };

  const emptyMessage =
    direction === "incoming"
      ? "No incoming threads match your search."
      : sentBy !== ""
        ? "No sent threads match the selected sender."
        : "No sent threads match your search.";

  return (
    <div className="grid h-full min-h-0 max-h-full grid-cols-1 md:grid-cols-[minmax(280px,38%)_1fr]">
      <div className="flex h-full min-h-0 flex-col overflow-hidden border-r border-slate-200 bg-white">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <EmailDirectionTabs
              direction={direction}
              onDirectionChange={handleDirectionChange}
            />
            {canMutateOrg ? (
              <EmailMailboxRefreshControl
                canSync={sync.canSync}
                isRefreshActive={isRefreshActiveForUi}
                statusLabel={refreshFeedback.statusLabel}
                statusTone={refreshFeedback.statusTone}
                showSuccess={refreshFeedback.showSuccess}
                onRefresh={handleRefresh}
              />
            ) : null}
          </div>
          {direction === "outgoing" ? (
            <EmailSentByFilter
              value={sentBy}
              onChange={handleSentByChange}
              users={mentionableUsers}
            />
          ) : null}
        </div>
        <OutlookSubscriptionDebugPanel />
        <div className="flex-1 overflow-y-auto">
          {listQuery.isPending && !listQuery.data ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              Loading mail…
            </p>
          ) : listQuery.isError ? (
            <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
              <p className="text-sm text-red-600">Failed to load emails.</p>
              <button
                type="button"
                onClick={() => void listQuery.refetch()}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
              >
                Try again
              </button>
            </div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500">
              {emptyMessage}
            </p>
          ) : (
            rows.map((row) => (
              <EmailThreadListRow
                key={row.gmailThreadId}
                row={row}
                direction={direction}
                selected={selectedThreadId === row.gmailThreadId}
                onSelect={() => setSelectedThreadId(row.gmailThreadId)}
                onInvoiceClick={(id) =>
                  onOpenInvoice ? onOpenInvoice(id) : navigate(`/invoice/${id}`)
                }
                onCustomerClick={(id) =>
                  onOpenCustomer
                    ? onOpenCustomer(id)
                    : navigate(`/customers/${id}`)
                }
              />
            ))
          )}
        </div>
        <ReceivablesPagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          isFetching={listQuery.isFetching}
          summary={
            <>
              {total} {direction === "incoming" ? "incoming" : "sent"} threads
            </>
          }
        />
      </div>
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <EmailThreadReadingPane
          gmailThreadId={selectedThreadId}
          thread={detailQuery.data}
          isLoading={detailQuery.isLoading && Boolean(selectedThreadId)}
          mailboxContext={mailboxContext}
          onReply={
            canMutateOrg
              ? (message, mode) => setReplyTarget({ message, mode })
              : undefined
          }
        />
      </div>
      {replyTarget && selectedThreadId ? (
        <EmailReplyModal
          opened
          onClose={() => setReplyTarget(null)}
          gmailThreadId={selectedThreadId}
          replyMode={replyTarget.mode}
          message={replyTarget.message}
          threadSubject={threadSubject}
          linkedInvoiceIds={selectedRow?.linkedInvoiceIds ?? []}
        />
      ) : null}
    </div>
  );
}
